import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { CheckableDropdownItem, Dropdown, DropdownButton } from "@similarweb/ui-components";

import { deviceIconMap } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import CountryService from "app/services/CountryService";

import type { FixedGscMarketConfig } from "../../types";
import { DropdownItem, DropdownWrapper } from "../styles";
import { dropdownTextSearchFilter } from "../utils";

type EnrichedMarketItem = {
    index: number;
    countryId: number;
    label: string;
    device: LocationDevice;
};

type Props = {
    marketConfigs: FixedGscMarketConfig[];
    selectedIndex: number;
    onSelect: (index: number) => void;
    disabledIndices?: number[];
    isDisabled?: boolean;
};

export const MarketDropdown = ({ marketConfigs, selectedIndex, onSelect, disabledIndices, isDisabled }: Props) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);

    const items: EnrichedMarketItem[] = useMemo(() => {
        return marketConfigs
            .map((config, index): EnrichedMarketItem | null => {
                const country = CountryService.getCountryByCode(config.countryCode);
                if (!country) {
                    return null;
                }
                return {
                    index,
                    countryId: country.id,
                    label: `${country.text} - ${config.language.text}`,
                    device: config.device,
                };
            })
            .filter((item): item is EnrichedMarketItem => item !== null);
    }, [marketConfigs]);

    const selectedItem = items.find((item) => item.index === selectedIndex) ?? items[0];

    const handleSelect = useCallback(
        (item: { id: string }) => {
            const index = Number(item.id);
            if (disabledIndices?.includes(index)) {
                return;
            }
            onSelect(index);
        },
        [onSelect, disabledIndices],
    );

    const placeholder = translate("rankTracker.gsc.marketDropdown.searchPlaceholder");

    const renderMarketRow = (item: EnrichedMarketItem) => {
        const DeviceIcon = deviceIconMap[item.device];
        return (
            <DropdownItem>
                <SWReactCountryIcons
                    countryCode={item.countryId}
                    size="22px"
                />
                <DeviceIcon
                    size={16}
                    color={colorsPalettes.carbon[200]}
                />
                {item.label}
            </DropdownItem>
        );
    };

    const isSingleMarket = marketConfigs.length === 1;
    const isDisabledDropdown = isSingleMarket || !!isDisabled;

    return (
        <DropdownWrapper
            data-automation="gsc-market-dropdown"
            hasValue
        >
            <Dropdown
                isOpen={isOpen}
                disabled={isDisabledDropdown}
                onToggle={setIsOpen}
                onClick={handleSelect}
                closeOnItemClick
                appendTo="body"
                hasSearch
                dropdownPopupPlacement="ontop-left"
                searchPlaceHolder={placeholder}
                searchFilter={dropdownTextSearchFilter}
            >
                {[
                    <DropdownButton
                        key="trigger"
                        height={40}
                        disabled={isDisabledDropdown}
                    >
                        {selectedItem ? renderMarketRow(selectedItem) : placeholder}
                    </DropdownButton>,
                    ...items.map((item) => (
                        <CheckableDropdownItem
                            key={item.index}
                            id={String(item.index)}
                            text={item.label}
                            disabled={disabledIndices?.includes(item.index)}
                        >
                            {renderMarketRow(item)}
                        </CheckableDropdownItem>
                    )),
                ]}
            </Dropdown>
        </DropdownWrapper>
    );
};
